import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { useState } from "react";
import { Label } from "@/components/atoms/Label";
import clsx from "clsx";
import { Check, Square, SquareCheckBig } from "lucide-react";

import { cn } from "@/lib/utils";

export interface CheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  checkClassName?: string;
}

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, checkClassName, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      "peer h-4 w-4 shrink-0 rounded-sm bg-white dark:bg-gray-200 border border-border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground",
      className
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      className={cn("flex items-center justify-center text-current")}
    >
      <Check className={clsx("h-4 w-4", checkClassName)} />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

interface CheckboxButtonProps extends CheckboxProps {
  variant?: "default" | "outline" | "text";
  labelClassName?: string;
}

const checkboxButtonVariants = {
  default: "bg-background",
  outline: "border border-border",
  text: "",
};

const checkboxButtonVariantsChecked = {
  default: "bg-primary !text-black",
  outline: "border-primary text-primary",
  text: "text-primary",
};

const CheckboxButton = ({
  children,
  className,
  labelClassName,
  variant = "default",
  onCheckedChange,
  ...props
}: CheckboxButtonProps) => {
  const [checked, setChecked] = useState(false);
  const classes = clsx(
    "relative p-3 rounded h-full",
    checkboxButtonVariants[variant],
    className,
    checked ? checkboxButtonVariantsChecked[variant] : ""
  );

  const checkboxClasses = clsx(
    "opacity-0 absolute left-0 top-0 w-full h-full",
    {"border-none" : variant === "text"}
  );

  const handleCheckedChange = (v: boolean) => {
    setChecked(v);
    onCheckedChange?.(v);
  };

  React.useEffect(() => {
    if ("checked" in props) setChecked(!!props.checked);
  }, [props?.checked]);

  return (
    <div className={classes}>
      <Label
        className={clsx(
          "flex items-center leading-normal h-full justify-between",
          labelClassName
        )}
      >
        <span className="pr-3">{children}</span>
        <Checkbox
          checked={checked}
          onCheckedChange={handleCheckedChange}
          className={checkboxClasses}
          checkClassName="opacity-0"
          {...props}
        />
        {checked ? <SquareCheckBig size={20} /> : <Square size={20} />}
      </Label>
    </div>
  );
};

export { CheckboxButton };
export default Checkbox;
